import React from 'react';
import { CheckCircle, Info, FileSpreadsheet, FileText } from 'lucide-react';
import { SOURCE_FORMATS } from './sourceFormats';

/**
 * Status card listing currently-supported acquisition source formats.
 * Renders above the uploader so operators see what they can upload today
 * and which formats are still coming.
 */
export const SupportedFormatsCard: React.FC = () => {
  // Hide the 'auto' meta-option from the support grid — it's just a switch.
  const visible = SOURCE_FORMATS.filter((f) => f.value !== 'auto');
  return (
    <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden mb-4">
      <div className="px-5 py-3 border-b border-slate-700/50 flex items-center gap-2">
        <Info className="w-4 h-4 text-indigo-400" />
        <h3 className="text-sm font-semibold text-white">
          Currently supported source formats
        </h3>
      </div>
      <div className="p-4 space-y-2">
        {visible.map((fmt) => {
          const isPdf = fmt.acceptedExtensions.includes('.pdf') && !fmt.acceptedExtensions.includes('.xlsx');
          const Icon = isPdf ? FileText : FileSpreadsheet;
          return (
            <div
              key={fmt.value}
              className="flex items-start gap-3 p-3 bg-slate-900/40 border border-slate-700/40 rounded-lg"
            >
              <Icon
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  isPdf ? 'text-red-400' : 'text-emerald-400'
                }`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="text-sm font-semibold text-white">
                    {fmt.label}
                  </span>
                  {fmt.supported ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      <CheckCircle className="w-2.5 h-2.5" /> Supported
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded bg-slate-500/20 text-slate-400 border border-slate-500/30">
                      Coming soon
                    </span>
                  )}
                  {fmt.supported && !fmt.hasBrokerLevelData && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      Entity-level only
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {fmt.description}
                </p>
                <p className="text-[10px] text-slate-500 mt-1 font-mono">
                  Accepts: {fmt.acceptedExtensions.join(', ')}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
